/**
 * P3-S3 룸 권위 리플레이(room-replay@1) 재생 — @picnic-field/room-sim 재생기(openRoomReplay)를 셸 룸 렌더 경로(expandMeta → 전시관 뷰)에 잇는다.
 *  - /replay/:id 상세의 meta.format 이 room-replay@1 이면 이 길(로컬 리플레이 ReplayFile v1 은 그대로 커널 호스트)
 *  - 재생기는 늦게 불러온다(커널 · Rapier). 가짜 모드(VITE_FIELD_FAKE=1)는 시간으로 흉내 낸 재생기(dev/fake/fakeRoomReplay)
 *  - 릴레이: relayTimeline() 로 구간 진행 막대 · 지금 구간 라벨(교사만 별명 · 학생은 '구간 n') · 실패 표시 없음(DR-01)
 *  - P3-S6 구간 분석(relayTimeline · teamResults = 모드 진행 재시뮬 한 번)은 Web Worker(roomAnalysis.worker.ts)에서 — {@link roomAnalysis}.
 *    Worker 를 못 쓰면(없음 · 불러오기 실패 · 코드 없는 오류) 메인 스레드 재생기로 대체한다. 결과는 같다(같은 코드 · 같은 입력).
 */
import type { TeamResult } from "@picnic-field/room-sim";
import type { RelayEvent } from "@picnic-field/modes";
import type { ReplayInfo } from "../../../lib/api/socialApi";
import { readConfig } from "../../../lib/config";
import type {
  RoomPlayer,
  RoomTimeline,
  OpenRoomPlayer,
  ToAnalysis,
  FromAnalysis,
  RoomAnalysisSource,
  RoomAnalysis,
  BarCell,
} from "../types";

export const ROOM_REPLAY_FORMAT = "room-replay@1";

/** 상세 메타가 룸 리플레이 머리인가 */
export function isRoomReplay(info: Pick<ReplayInfo, "meta">): boolean {
  const m = info.meta as { format?: unknown } | null | undefined;
  return m?.format === ROOM_REPLAY_FORMAT;
}

let override: OpenRoomPlayer | null = null;

/** 재생기 열기 — 기본은 room-sim(늦게 불러옴) · 가짜 모드는 흉내 */
export const openRoomPlayer: OpenRoomPlayer = async (o) => {
  if (override) return override(o);
  if (import.meta.env.DEV && readConfig().fake)
    return (await import("../../../dev/fake/fakeRoomReplay")).fakeOpenRoomReplay(o);
  const { openRoomReplay } = await import("@picnic-field/room-sim");
  return openRoomReplay(o);
};

/** 시험 전용 — null 이면 기본값으로 */
export function setOpenRoomPlayerForTests(f: OpenRoomPlayer | null) {
  override = f;
}

// ── P3-S6 구간 분석 Worker ─────────────────────────────────────────────────

/** 재생기 오류(코드 있음) — Worker 가 돌려준 것도 같은 모양으로 */
class AnalysisError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "RoomReplayError";
  }
}

type MakeWorker = () => Worker | null;

const defaultWorker: MakeWorker = () =>
  typeof Worker === "undefined"
    ? null
    : new Worker(new URL("./roomAnalysis.worker.ts", import.meta.url), { type: "module" });

let workerOverride: MakeWorker | null | undefined;

/** 시험 전용 — Worker 만들기 바꾸기(null = Worker 없음 · undefined = 기본값) */
export function setAnalysisWorkerForTests(f: MakeWorker | null | undefined) {
  workerOverride = f;
}

/** 이 재생기를 Worker 로 분석해도 되나 — 기본 room-sim 재생기일 때만(가짜 · 시험 재생기는 room-sim 이 아니다) */
function workerFor(): MakeWorker | null {
  if (workerOverride !== undefined) return workerOverride;
  if (override) return null;
  if (import.meta.env.DEV && readConfig().fake) return null;
  return defaultWorker;
}

/** Worker 한 번 부르기 — 코드 있는 오류는 그대로 · 그 밖(불러오기 실패 등)은 null(메인 대체) */
function ask(
  make: MakeWorker,
  src: RoomAnalysisSource,
  what: ToAnalysis["what"],
  track: Map<Worker, () => void>,
) {
  return new Promise<FromAnalysis | null>((ok, no) => {
    let w: Worker | null;
    try {
      w = make();
    } catch {
      w = null;
    }
    if (!w) return ok(null);
    const worker = w;
    const end = () => {
      track.delete(worker);
      worker.terminate();
    };
    // 닫으면(dispose) 기다리던 쪽은 null 을 받는다
    track.set(worker, () => {
      end();
      ok(null);
    });
    worker.onmessage = (ev: MessageEvent<FromAnalysis>) => {
      end();
      const m = ev.data;
      if (m.type === "error") {
        if (m.code) no(new AnalysisError(m.code, m.message));
        else ok(null);
      } else ok(m);
    };
    worker.onerror = (ev) => {
      ev.preventDefault?.();
      end();
      ok(null);
    };
    const bytes = src.bytes.slice().buffer;
    const msg: ToAnalysis = {
      what,
      bytes,
      bundles: src.bundles,
      runtimeConfig: src.runtimeConfig ?? null,
      base: typeof location !== "undefined" ? location.href : "http://localhost/",
    };
    try {
      worker.postMessage(msg, [bytes]);
    } catch {
      end();
      ok(null);
    }
  });
}

/**
 * 구간 분석 — Worker 에서 먼저, 안 되면 메인 스레드 재생기(player.relayTimeline · teamResults)로.
 * Worker 결과(이벤트)로 타임라인을 다시 만든다(room-sim relayTimelineOf — 메인이 만든 것과 같다).
 */
export function roomAnalysis(player: RoomPlayer, src: RoomAnalysisSource | null): RoomAnalysis {
  const make = src ? workerFor() : null;
  const live = new Map<Worker, () => void>();
  let disposed = false;
  let where: RoomAnalysis["where"] = null;
  let tl: Promise<RoomTimeline> | null = null;
  let tm: Promise<TeamResult[]> | null = null;
  const fallback = <T>(f: () => Promise<T>) => {
    where ??= "main";
    return f();
  };
  return {
    get where() {
      return where;
    },
    timeline() {
      tl ??= (async () => {
        if (make && src && player.mode.kind === "relay") {
          const r = await ask(make, src, "relay", live);
          if (r?.type === "relay") {
            where = "worker";
            const { relayTimelineOf } = await import("@picnic-field/room-sim");
            return relayTimelineOf(r.events, r.verified);
          }
        }
        if (disposed) throw new AnalysisError("disposed", "분석을 닫았다");
        return fallback(() => player.relayTimeline());
      })();
      return tl;
    },
    teams() {
      tm ??= (async () => {
        const full = player as RoomPlayer & { teamResults?: () => Promise<TeamResult[]> };
        if (make && src && player.mode.kind === "carry") {
          const r = await ask(make, src, "teams", live);
          if (r?.type === "teams") {
            where = "worker";
            return r.teams;
          }
        }
        if (disposed) throw new AnalysisError("disposed", "분석을 닫았다");
        return fallback(async () => (full.teamResults ? full.teamResults() : []));
      })();
      return tm;
    },
    dispose() {
      disposed = true;
      for (const stop of [...live.values()]) stop();
    },
  };
}

/**
 * 그 틱의 구간 막대 — 이벤트(tick ≤ t) 반영. done · skipped 는 끝난 칸, 지금 구간(armed · running · failed · retry)은 current.
 * 실패는 따로 표시하지 않는다(학생 · 교사 재생 모두 — 영상과 같은 원칙).
 */
export function relayBar(events: readonly RelayEvent[], n: number, tick: number): BarCell[] {
  const cells: BarCell[] = new Array(n).fill("waiting");
  let cur: number | null = null;
  for (const e of events) {
    if (e.tick > tick) break;
    if (e.kind === "done" && e.stage < n) {
      cells[e.stage] = "done";
      if (cur === e.stage) cur = null;
    } else if (e.kind === "skipped" && e.stage < n) {
      cells[e.stage] = "skipped";
      if (cur === e.stage) cur = null;
    } else if (
      (e.kind === "armed" || e.kind === "running" || e.kind === "failed" || e.kind === "retry") &&
      e.stage < n
    )
      cur = e.stage;
  }
  if (cur !== null && cells[cur] === "waiting") cells[cur] = "current";
  return cells;
}

/** 구간 i 로 가기 — 그 구간 첫 armed(없으면 running · 건너뜀 틱) · 모르면 null */
export function stageStartTick(events: readonly RelayEvent[], stage: number): number | null {
  for (const e of events)
    if ((e.kind === "armed" || e.kind === "running" || e.kind === "skipped") && e.stage === stage)
      return e.tick;
  return null;
}

/** 틱 → 초(60 Hz) */
export const ticksToS = (t: number) => t / 60;
